import createNetworkClient, {
  createAuthMiddleware,
  type StandardNetworkError,
  type RequestConfig,
  type NetworkResponse,
  type RequestMiddleware,
  type ResponseMiddleware,
} from '@onxmaps/x-http-client'
import pubsub from '@/lib/pubsub'
import { VERSION } from '@/lib/defines'
import { getConsistencyToken, setConsistencyToken } from '@/lib/net'

import { getAccessToken } from '@/services/identity-client'

/**
 * Event name for session expired modal
 */
export const SESSION_EXPIRED_EVENT = 'show_session_expired_modal'

/**
 * Token getter function that retrieves the current access token
 */
export const getToken = async (): Promise<string> => {
  return (await getAccessToken()) || ''
}

/**
 * Custom error middleware to handle 401 (unauthenticated) responses
 */
export const unauthorizedErrorMiddleware = (
  error: StandardNetworkError
): StandardNetworkError => {
  // Check if this is a 401 (unauthenticated) error
  if (error.status === 401) {
    // show session expired modal
    pubsub.publish(SESSION_EXPIRED_EVENT)
  }
  return error
}

/**
 * Request middleware to add ONX application headers to outgoing requests
 */
export const onxHeadersRequestMiddleware: RequestMiddleware = (
  config: RequestConfig
): RequestConfig => ({
  ...config,
  headers: {
    ...config.headers,
    'ONX-Application-ID': 'fish',
    'ONX-Application-Platform': 'web',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    'ONX-Application-Version': VERSION!,
  },
})

/**
 * Request middleware to add consistency token to outgoing requests
 */
export const consistencyTokenRequestMiddleware: RequestMiddleware = (
  config: RequestConfig
): RequestConfig => {
  const token: string | null = getConsistencyToken()
  if (token) {
    return {
      ...config,
      headers: {
        ...config.headers,
        'Onx-Consistency-Token': token,
      },
    }
  }
  return config
}

/**
 * Response middleware to extract and store consistency token from responses
 */
export const consistencyTokenResponseMiddleware: ResponseMiddleware = <T>(
  response: NetworkResponse<T>
): NetworkResponse<T> => {
  const token = response.headers?.['onx-consistency-token']
  if (token) {
    setConsistencyToken(token)
  }
  return response
}

/**
 * Creates and configures the HTTP client with auth and error handling
 */
export const createHttpClient = () => {
  // Create auth middleware with token management
  const authMiddleware = createAuthMiddleware(getToken)

  // Create and configure the HTTP client
  return createNetworkClient({
    baseUrl: window.environment.apiUrl,
    requestMiddlewares: [
      authMiddleware.requestMiddleware,
      onxHeadersRequestMiddleware,
      consistencyTokenRequestMiddleware,
    ],
    responseMiddlewares: [consistencyTokenResponseMiddleware],
    errorMiddlewares: [unauthorizedErrorMiddleware],
  })
}

/**
 * Export the configured HTTP client for use in the application.
 *
 * @remarks This basic client instance is currently only meant to be
 * used in conjunction with the {@link https://github.com/onxmaps/markups-web | @onxmaps/markups-web} package which
 * requires it as a peer dependency.
 *
 * For all other use cases further configuration will be necessary
 * to match behavior of current @/lib/net.ts HTTP client implementation.
 */
export default createHttpClient()
